import { createPortal } from 'react-dom';
import { useState, useEffect, useCallback } from 'react';

import { Box } from '@mui/system';

import { useBoolean } from 'src/hooks/use-boolean';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { localStorageGetItem, localStorageSetItem } from 'src/utils/local-storage';

import { PageOptions } from 'src/services/api.types';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { isOrganisationTenant } from 'src/services/auth/auth.utils';
import { ConnectionStatus } from 'src/services/connections/connections.types';
import { hideAccountSwitcher } from 'src/store/slices/accounts/accountsSlice';
import { useGetIncomingJobInvitesQuery } from 'src/services/jobs/jobs.service';
import { showCandidateJobRequestsModal } from 'src/store/slices/jobs/jobsSlice';
import { useGetIncomingConnectionRequestsQuery } from 'src/services/connections/connections.service';
import { toggleCandidateFileImporterModal, setAvailabilityManagerModalVisibility } from 'src/store/slices/candidate/candidateSlice';
import { closeTaskViewer, closeDocumentViewer, closeCandidateViewer, closeMultiMailerDialog, closeSingleMailerDialog, closeUserFeedbackDialog, closeTaskEditorSidesheet } from 'src/store/slices/app/appSlice';

import AccountInfoDialog from 'src/components/account-viewer/account-info-dialog';
import WindowedDocumentViewer from 'src/components/documents/document-viewer-window';
import StackedRecordOverlay from 'src/components/record-panel/stacked-record-overlay';

import TaskViewer from 'src/sections/tasks/components/task-viewer';
import AccountSwitcherModal from 'src/sections/app/account-switcher';
import EmailSenderDialog from 'src/sections/email-sync/email-sender';
import ImportJobModal from 'src/sections/jobs/modals/import-job-modal';
import UserFeedbackDialog from 'src/sections/app/user-feedback-dialog';
import EmailMultiSender from 'src/sections/email-sync/email-multi-sender';
import JobApprovalModal from 'src/sections/jobs/modals/job-approval-modal';
import MaintenanceModeOverlay from 'src/sections/app/maintenance-mode-overlay';
import JobLinkActionModal from 'src/sections/jobs/modals/job-link-action-modal';
import FeatureAccessModal from 'src/sections/billing/modals/feature-access-modal';
import RequestJobChangesModal from 'src/sections/jobs/modals/request-job-changes';
import PlacementEditor from 'src/sections/jobs/placements/modals/placement-editor';
import ActionJobInviteModal from 'src/sections/jobs/modals/action-job-invite-modal';
import CalendarEventViewer from 'src/sections/calendar/modal/calendar-event-viewer';
import CallOutcomeOverlay from 'src/sections/call-lists/modals/call-outcome-overlay';
import TaskEditorSideSheet from 'src/sections/tasks/sidesheet/task-editor-sidesheet';
import PendingJobInviteModal from 'src/sections/jobs/modals/pending-job-invite-modal';
import CandidateFileImporter from 'src/sections/candidates/modal/candidate-file-importer';
import WindowedCandidateViewer from 'src/sections/candidates/modal/candidate-viewer-window';
import OfferEditorModal from 'src/sections/jobs/applications/modals/offer/offer-editor-modal';
import CandidateJobRequestsModal from 'src/sections/jobs/modals/candidate-job-requests-modal';
import CompanyInvitesListModal from 'src/sections/candidates/modal/company-invites-list-modal';
import CompanyEditorSideSheet from 'src/sections/companies/sidesheet/company-editor.sidesheet';
import CandidateInvitesListModal from 'src/sections/candidates/modal/candidate-invites-list-modal';
import InviteCompanyConnectModal from 'src/sections/candidates/modal/invite-company-connect-modal';
import CandidateEditorSideSheet from 'src/sections/candidates/sidesheet/candidate-editor-sidesheet';
import CandidateOfferModal from 'src/sections/jobs/applications/modals/offer/candidate-offer-modal';
import AvailabilityManagementModal from 'src/sections/profile/modals/availability-management-modal';
import InviteCandidateConnectModal from 'src/sections/candidates/modal/invite-candidate-connect-modal';
import ActionInterviewModal from 'src/sections/jobs/applications/modals/interview/action-interview-modal';
import ManagerOfferActionModal from 'src/sections/jobs/applications/modals/offer/manager-offer-action-modal';
import ScheduleInterviewModal from 'src/sections/jobs/applications/modals/interview/schedule-interview-modal';
import NotifyOfferCandidateModal from 'src/sections/jobs/applications/modals/offer/notify-offer-candidate-modal';
import ManageConnectionRequestsModal from 'src/sections/admin/connections/modal/manage-connection-requests-modal';
import ConnectionActionModal from 'src/sections/connections/modal/connection-action-modal/connection-action-modal';
import CreateConnectionModal from 'src/sections/connections/modal/create-connection-modal/create-connection-modal';
import ActionStageChangeModal from 'src/sections/jobs/applications/modals/stage-change-requests/action-stage-change-modal';
import JobStageChangeRequestsModal from 'src/sections/jobs/applications/modals/stage-change-requests/stage-change-requests-modal';

import { TenantType } from 'src/types/enums';

const DEFAULT_PAGE_REQUESTS = {
  page: 1,
  per_page: 5,
};

export default function ModalContainer() {
  const authContext = useAuthContext();
  const tenantType = useOrgTenant();
  const dispatch = useAppDispatch();

  const [incomingRequestsPage, setIncomingRequestsPage] =
    useState<PageOptions>(DEFAULT_PAGE_REQUESTS);

  const { currentData: incomingRequestsData } = useGetIncomingConnectionRequestsQuery({
    organization_id: authContext.organization.id,
    status: ConnectionStatus.PENDING,
    ...incomingRequestsPage,
  });

  const { currentData: pendingJobInvites } = useGetIncomingJobInvitesQuery(
    {
      page: 1,
      per_page: 1,
    },
    { skip: tenantType !== TenantType.Candidate }
  );

  useEffect(() => {
    if (!pendingJobInvites?.count || !authContext.account.onboarding_context?.signup_flow) return;

    dispatch(showCandidateJobRequestsModal());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pendingJobInvites]);

  const showIncomingRequestsModal = useBoolean();

  const changeIncomingReqestPage = useCallback(
    (page: number) => {
      setIncomingRequestsPage({
        ...incomingRequestsPage,
        page,
      });
    },
    [incomingRequestsPage]
  );

  const currentJobId = useAppSelector((state) => state.jobs.currentJobId);

  const showConnectionActionModal = useAppSelector(
    (state) => state.connectionsState.showConnectionModal
  );

  const showCreateConnectionModal = useAppSelector(
    (state) => state.connectionsState.showCreateConnectionModal
  );

  const candidateFileImporterState = useAppSelector(
    (state) => state.candidate.fileImporterState
  );

  const showCompanyEditorSidesheet = useAppSelector((state) => state.company.showCompanyEditor);
  const currentCompanyId = useAppSelector((state) => state.company.currentCompanyId);

  const candidateEditorState = useAppSelector((state) => state.candidate.candidateEditorState);

  const taskSidesheetState = useAppSelector((state) => state.app.taskSidesheet);

  const taskViewerState = useAppSelector((state) => state.app.taskViewer);

  const showJobLinkActionModal = useAppSelector((state) => state.jobs.showJobLinkActionModal);

  const showJobVerificationModal = useAppSelector((state) => state.jobs.showJobVerificationModal);

  const mailerDialogState = useAppSelector((state) => state.app.singleMailerDialog);

  const multiMailerDialogState = useAppSelector((state) => state.app.multiMailerDialog);

  const accountViewerState = useAppSelector((state) => state.app.accountViewer);

  const placementEditorState = useAppSelector((state) => state.jobs.placementEditorState);

  const calendarEventViewerState = useAppSelector((state) => state.app.calendarEventViewer);

  const shouldShowCandidateJobRequestsModal = useAppSelector(
    (state) => state.jobs.showCandidateJobRequestsModal
  );

  const showCandidateInviteListModal = useAppSelector(
    (state) => state.candidate.showCandidateInviteListModal
  );

  const showCandidateInviteModal = useAppSelector(
    (state) => state.candidate.showCandidateInviteModal
  );

  const showCompanyInviteActionModal = useAppSelector(
    (state) => state.candidate.showCompanyInviteListModal
  );

  const showCandidateJobInviteActionModal = useAppSelector(
    (state) => state.jobs.showCandidateJobInviteActionModal
  );

  const showPendingJobInvitesModal = useAppSelector(
    (state) => state.jobs.showPendingJobInvitesModal
  );

  const showImportJobModal = useAppSelector((state) => state.jobs.showImportJobModal);

  const showActionStageChangeRequest = useAppSelector(
    (state) => state.applications.showActionStageChangeRequest
  );

  const showJobStageChangeRequestsModal = useAppSelector(
    (state) => state.applications.showJobStageChangeRequestsModal
  );

  const showCreateOfferModal = useAppSelector((state) => state.applications.showOfferModal);

  const showCandidateOfferModal = useAppSelector(
    (state) => state.applications.showCandidateOfferModal
  );

  const showNotifyCandidateOfferModal = useAppSelector(
    (state) => state.applications.showNotifyCandidateOfferModal
  );

  const showManagerOfferActionModal = useAppSelector(
    (state) => state.applications.showManagerOfferActionModal
  );

  const maintenanceModeState = useAppSelector((state) => state.app.maintenanceMode);

  const showFeatureAccessModal = useAppSelector((state) => state.billing.featureAccessModal.open);

  const showRequestJobChangesModal = useAppSelector(
    (state) => state.jobs.showRequestJobChangesModal
  );

  const showAvailabilityManagerModal = useAppSelector(
    (state) => state.candidate.showAvailabilityManagerModal
  );

  const showCompanyInviteModal = useAppSelector((state) => state.candidate.showCompanyInviteModal);
  const companyInviteTargetTenant = useAppSelector((state) => state.candidate.companyInviteTargetTenant);

  const showScheduleInterviewModal = useAppSelector((state) => state.interviews.showInterviewModal);

  const selectedInterviewApplicationId = useAppSelector(
    (state) => state.interviews.selectedInterviewApplicationId
  );

  const showInterviewActionModal = useAppSelector(
    (state) => state.interviews.showInterviewActionModal
  );

  const selectedInterviewId = useAppSelector((state) => state.interviews.selectedInterviewId);

  const documentViewerVisible = useAppSelector((state) => state.app.documentViewer.open);

  const candidateViewerVisible = useAppSelector((state) => state.app.candidateViewer.open);

  const accountSwitcherVisibility = useAppSelector(
    (state) => state.accounts.account_switcher_visible
  );

  const showUserFeedbackDialog = useAppSelector((state) => state.app.userFeedbackDialog.open);

  useEffect(() => {
    if (!incomingRequestsData?.count) return;
    const shouldShowModal = localStorageGetItem('show_incoming_requests_modal');

    if (shouldShowModal === 'false') return;

    showIncomingRequestsModal.onTrue();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingRequestsData]);

  const onIncomingRequestsModalClose = () => {
    showIncomingRequestsModal.onFalse();
    localStorageSetItem('show_incoming_requests_modal', 'false');
  };

  const handleCloseAvailabilityManagerModal = () => {
    dispatch(setAvailabilityManagerModalVisibility({ status: false }));
  };

  const handleCloseCandidateFileImporter = () => {
    dispatch(toggleCandidateFileImporterModal({ status: false }));
  };

  const handleCloseAccountSwitcher = () => {
    dispatch(hideAccountSwitcher());
  };

  const handleCloseDocumentViewer = () => {
    dispatch(closeDocumentViewer())
  };

  const handleCloseCandidateViewer = () => {
    dispatch(closeCandidateViewer())
  }

  const handlecloseSingleMailerDialog = () => {
    dispatch(closeSingleMailerDialog())
  }

  const handleCloseMultiMailerDialog = () => {
    dispatch(closeMultiMailerDialog())
  }

  const handleCloseTaskEditor = useCallback(() => {
    dispatch(closeTaskEditorSidesheet());
  }, [dispatch]);

  const handleCloseTaskViewer = useCallback(() => {
    dispatch(closeTaskViewer());
  }, [dispatch]);

  const handleCloseUserFeedbackDialog = useCallback(() => {
    dispatch(closeUserFeedbackDialog());
  }, [dispatch]);

  return (
    <Box id="modal-container">
      {isOrganisationTenant(tenantType) && (
        <ConnectionActionModal open={showConnectionActionModal} />
      )}

      <CreateConnectionModal open={showCreateConnectionModal} />

      <JobLinkActionModal open={showJobLinkActionModal} />

      {isOrganisationTenant(tenantType) && <JobApprovalModal open={showJobVerificationModal} />}

      <CandidateJobRequestsModal open={shouldShowCandidateJobRequestsModal} />

      {showCandidateInviteListModal && (
        <CandidateInvitesListModal open={showCandidateInviteListModal} />
      )}

      {showCompanyInviteActionModal && (
        <CompanyInvitesListModal open={showCompanyInviteActionModal} />
      )}

      {showCompanyInviteModal && (
        <InviteCompanyConnectModal open={showCompanyInviteModal} targetType={companyInviteTargetTenant as TenantType} />
      )}

      {
        showCandidateInviteModal && (
          <InviteCandidateConnectModal open={showCandidateInviteModal} />
        )
      }

      {showCandidateJobInviteActionModal && (
        <ActionJobInviteModal open={showCandidateJobInviteActionModal} />
      )}

      <PendingJobInviteModal
        open={showPendingJobInvitesModal}
        scopedJobId={currentJobId}
      />

      <CompanyEditorSideSheet
        open={showCompanyEditorSidesheet}
        profileId={currentCompanyId}
      />

      <CandidateEditorSideSheet
        open={candidateEditorState.open}
        candidateId={candidateEditorState.candidateId || null}
      />

      <ImportJobModal open={showImportJobModal} />

      <FeatureAccessModal open={showFeatureAccessModal} />

      <CandidateFileImporter organizationId={candidateFileImporterState?.organizationId as string} open={candidateFileImporterState.open} onClose={handleCloseCandidateFileImporter} />

      <ManageConnectionRequestsModal
        open={showIncomingRequestsModal.value}
        onClose={onIncomingRequestsModalClose}
        type="incoming"
        requests={incomingRequestsData}
        page={incomingRequestsPage}
        changePage={changeIncomingReqestPage}
      />

      <TaskEditorSideSheet
        open={taskSidesheetState.open}
        onClose={handleCloseTaskEditor}
        taskId={taskSidesheetState.taskId}
      />

      {
        taskViewerState.taskId && (
          <TaskViewer taskId={taskViewerState.taskId} open={taskViewerState.open} onClose={handleCloseTaskViewer} />
        )
      }

      <ActionStageChangeModal open={showActionStageChangeRequest} />

      <JobStageChangeRequestsModal open={showJobStageChangeRequestsModal} />

      <OfferEditorModal open={showCreateOfferModal} />

      {showCandidateOfferModal && <CandidateOfferModal open={showCandidateOfferModal} />}

      {showManagerOfferActionModal && <ManagerOfferActionModal open={showManagerOfferActionModal} />}

      {maintenanceModeState && <MaintenanceModeOverlay />}

      {showRequestJobChangesModal && <RequestJobChangesModal open={showRequestJobChangesModal} />}

      {
        placementEditorState.open && (
          <PlacementEditor
            open={placementEditorState.open}
          />
        )
      }

      {
        showNotifyCandidateOfferModal && (
          <NotifyOfferCandidateModal
            open={showNotifyCandidateOfferModal}
          />
        )
      }

      <AvailabilityManagementModal
        open={showAvailabilityManagerModal}
        onClose={handleCloseAvailabilityManagerModal}
      />

      {selectedInterviewApplicationId && showScheduleInterviewModal && (
        <ScheduleInterviewModal applicationId={selectedInterviewApplicationId} />
      )}

      {selectedInterviewId && showInterviewActionModal && <ActionInterviewModal open />}

      <AccountSwitcherModal open={accountSwitcherVisibility} onClose={handleCloseAccountSwitcher} />

      {
        documentViewerVisible &&
        createPortal(
          <WindowedDocumentViewer onClose={handleCloseDocumentViewer} />,
          document.body
        )
      }


      {
        candidateViewerVisible &&
        createPortal(
          <WindowedCandidateViewer onClose={handleCloseCandidateViewer} />,
          document.body
        )
      }

      {/* JT1-1736: a linked deal / contact / company opened on top of the record in view */}
      <StackedRecordOverlay />

      {/* JT1-1827: log a call placed from a profile against the caller's call list */}
      <CallOutcomeOverlay />

      {
        mailerDialogState.open && <EmailSenderDialog
          open={mailerDialogState.open}
          templateType={mailerDialogState.templateType}
          templateEntityId={mailerDialogState.templateEntityId}
          onClose={handlecloseSingleMailerDialog}
          emails={mailerDialogState.emails || []}
          targetType={mailerDialogState.targetType}
          targetId={mailerDialogState.targetId}
          title={mailerDialogState.title}
          subject={mailerDialogState.subject}
          body={mailerDialogState.body}
        />
      }

      {
        multiMailerDialogState.open && <EmailMultiSender />
      }

      <UserFeedbackDialog
        open={showUserFeedbackDialog}
        onClose={handleCloseUserFeedbackDialog}
      />

      {calendarEventViewerState.open && (
        <CalendarEventViewer
          open={calendarEventViewerState.open}
          eventId={calendarEventViewerState.eventId as string}
        />
      )}

      {
        accountViewerState.open && (
          <AccountInfoDialog
            open={accountViewerState.open}
            accountId={accountViewerState.accountId as string}
          />
        )
      }

    </Box>
  );
}
